import { useCallback, useState, type FormEvent, type ReactElement } from "react";
import { Pencil, Plus, Trash2, Users } from "lucide-react";
import { space } from "@tokens/index";
import { Badge, Button, ConfirmDialog, Surface, Text, TextInput } from "@primitives/index";
import {
    AdminSection,
    EmptyLine,
    Field,
    FormActions,
    MutationError,
    RowActions,
} from "../admin-ui";
import type { RowAction } from "../admin-ui";
import { useCreateTeam, useDeleteTeam, useRenameTeam } from "../management-groups";
import type { ManagementGroup, Team } from "../management-groups";
import { cancelOnEscape } from "./escapeCancels";
import { TeamMembersEditor } from "./TeamMembersEditor";
import { TeamQueuePushPanel } from "./TeamQueuePushPanel";

/**
 * Teams block: one card per team with its roster and its push/withdraw
 * controls, plus the create form.
 */

interface TeamCardProps {
    readonly groupId: string;
    readonly team: Team;
}

function TeamCard({ groupId, team }: TeamCardProps): ReactElement {
    const rename = useRenameTeam();
    const remove = useDeleteTeam();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(team.name);
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    const handleRename = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            const trimmed = draft.trim();
            if (trimmed.length === 0) return;
            rename.mutate(
                { id: groupId, teamId: team.id, name: trimmed },
                { onSuccess: () => setEditing(false) },
            );
        },
        [rename, groupId, team.id, draft],
    );

    const cancelRename = useCallback(() => {
        if (rename.isPending) return;
        setEditing(false);
        setDraft(team.name);
    }, [rename.isPending, team.name]);

    const confirmDelete = useCallback(() => {
        setConfirmingDelete(false);
        remove.mutate({ id: groupId, teamId: team.id });
    }, [remove, groupId, team.id]);

    const actions: readonly RowAction[] = [
        {
            key: "rename",
            label: "Rename",
            icon: Pencil,
            onSelect: () => {
                setDraft(team.name);
                setEditing(true);
            },
        },
        {
            key: "delete",
            label: "Delete",
            icon: Trash2,
            onSelect: () => setConfirmingDelete(true),
            disabled: remove.isPending,
            tone: "danger",
        },
    ];

    return (
        <AdminSection
            title={editing ? undefined : team.name}
            icon={editing ? undefined : Users}
            count={editing ? undefined : team.members.length}
            trailing={
                editing ? undefined : (
                    <RowActions actions={actions} menuLabel={`Actions for ${team.name}`} />
                )
            }
        >
            {editing && (
                <form
                    onSubmit={handleRename}
                    onKeyDown={cancelOnEscape(cancelRename)}
                    style={{ display: "grid", gap: space[3] }}
                >
                    <Field label="Team name">
                        {(id) => (
                            <TextInput
                                id={id}
                                autoFocus
                                fullWidth
                                value={draft}
                                disabled={rename.isPending}
                                onChange={(e) => setDraft(e.target.value)}
                            />
                        )}
                    </Field>
                    <FormActions
                        primary={
                            <Button
                                type="submit"
                                variant="primary"
                                size="md"
                                disabled={draft.trim().length === 0}
                                busy={rename.isPending}
                            >
                                Save
                            </Button>
                        }
                        secondary={
                            <Button
                                type="button"
                                variant="ghost"
                                size="md"
                                onClick={cancelRename}
                                disabled={rename.isPending}
                            >
                                Cancel
                            </Button>
                        }
                    />
                </form>
            )}
            <MutationError error={rename.error ?? remove.error} />
            <TeamMembersEditor groupId={groupId} team={team} />
            <TeamQueuePushPanel groupId={groupId} teamId={team.id} />
            <ConfirmDialog
                open={confirmingDelete}
                title="Delete team"
                message={`Delete team '${team.name}'?`}
                confirmLabel="Delete"
                tone="danger"
                onConfirm={confirmDelete}
                onCancel={() => setConfirmingDelete(false)}
            />
        </AdminSection>
    );
}

export function TeamsSection({ group }: { readonly group: ManagementGroup }): ReactElement {
    const create = useCreateTeam();
    const [composing, setComposing] = useState(false);
    const [name, setName] = useState("");

    const handleCreate = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            const trimmed = name.trim();
            if (trimmed.length === 0) return;
            create.mutate(
                { id: group.id, name: trimmed },
                {
                    onSuccess: () => {
                        setName("");
                        setComposing(false);
                    },
                },
            );
        },
        [create, group.id, name],
    );

    const cancelCreate = useCallback(() => {
        if (create.isPending) return;
        setComposing(false);
        setName("");
    }, [create.isPending]);

    const newTeamButton = (
        <Button variant="primary" size="sm" onClick={() => setComposing(true)}>
            <Plus size={14} strokeWidth={2.25} aria-hidden="true" />
            New team
        </Button>
    );

    return (
        <section aria-label="Teams" style={{ display: "grid", gap: space[3], minWidth: 0 }}>
            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: space[3],
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: space[2], minWidth: 0 }}>
                    <Users size={16} strokeWidth={2} aria-hidden="true" />
                    <Text as="h2" role="subheading">
                        Teams
                    </Text>
                    <Badge variant="neutral" size="sm" caps={false}>
                        {group.teams.length}
                    </Badge>
                </div>
                {!composing && newTeamButton}
            </div>
            {composing && (
                <Surface elevation={1} borderRadius="lg" padding={4} style={{ width: "100%" }}>
                    <form
                        onSubmit={handleCreate}
                        onKeyDown={cancelOnEscape(cancelCreate)}
                        style={{ display: "grid", gap: space[3] }}
                    >
                        <Field label="Team name">
                            {(id) => (
                                <TextInput
                                    id={id}
                                    autoFocus
                                    fullWidth
                                    placeholder="Team name"
                                    value={name}
                                    disabled={create.isPending}
                                    onChange={(e) => setName(e.target.value)}
                                />
                            )}
                        </Field>
                        <MutationError error={create.error} />
                        <FormActions
                            primary={
                                <Button
                                    type="submit"
                                    variant="primary"
                                    size="md"
                                    disabled={name.trim().length === 0}
                                    busy={create.isPending}
                                >
                                    Create
                                </Button>
                            }
                            secondary={
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="md"
                                    onClick={cancelCreate}
                                    disabled={create.isPending}
                                >
                                    Cancel
                                </Button>
                            }
                        />
                    </form>
                </Surface>
            )}
            {group.teams.length === 0 && !composing && <EmptyLine>No teams yet.</EmptyLine>}
            {group.teams.map((t) => (
                <TeamCard key={t.id} groupId={group.id} team={t} />
            ))}
        </section>
    );
}
